import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { createStepUpResponseErrorHandler } from "@ridereserv/shared/http/step-up";
import { supabase } from "./supabase";
import { signOutCurrentSession } from "./authSession";
import { useAuthStore } from "@/stores/authStore";
import { requestStepUp } from "@/stores/stepUpStore";
import { env } from "./env";
import type {
  Address,
  SubscriptionTier,
  TierFeatureFlag,
  TierLimit,
  TierRate,
} from "@ridereserv/shared/types";
import type { VehicleCapabilityStatus } from "@ridereserv/shared/utils";

const API_URL = env.VITE_API_URL ?? "http://localhost:3000/api";

/**
 * API client configured for platform endpoints with auth interceptor.
 */
export const api: AxiosInstance = axios.create({
  baseURL: `${API_URL}/platform`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to every request
api.interceptors.request.use(
  async (config) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`;
    }
    return config;
  },
  (error: unknown) => Promise.reject(error instanceof Error ? error : new Error(String(error)))
);

const handleResponseError = createStepUpResponseErrorHandler({
  requestStepUp,
  retry: (config: InternalAxiosRequestConfig) => api.request(config),
  onUnhandled: async (error: unknown) => {
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 401) {
      const { isLoading } = useAuthStore.getState();

      // Only clear auth if we're past the initialization phase.
      // During initialization, Supabase may not have hydrated from localStorage yet,
      // causing requests to go without auth headers. We don't want to clear valid
      // persisted auth state just because the initial requests fail.
      if (!isLoading) {
        useAuthStore.getState().clearAuth();
        await signOutCurrentSession();
        // Only redirect if not already on login page to prevent infinite loop
        if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(axiosError);
  },
});

// Handle shared step-up retries before applying the admin-owned 401 policy.
api.interceptors.response.use((response) => response, handleResponseError);

// Type definitions for API responses
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * GET a resource and unwrap the standard `{ success, data }` envelope.
 * @param url - The request URL (path plus any query string)
 * @returns The unwrapped `data` payload
 */
async function apiGet<T>(url: string): Promise<T> {
  const response = await api.get<ApiResponse<T>>(url);
  return response.data.data;
}

// Platform admin type
export interface PlatformAdmin {
  id: string;
  email: string;
  name: string;
  role: string;
  permissions: string[];
  isActive: boolean;
  lastLoginAt: string | null;
}

// Driver request type (certification model)
export interface DriverRequest {
  id: string;
  email: string | null;
  name: string;
  phone: string | null;
  driverApprovalStatus: string;
  driverRegistrationInfo: {
    // Drivers now choose their plan AFTER approval, so the registration record
    // carries no plan for real applicants — `null`. (Legacy/seed rows may still
    // hold an explicit `free_driver` / `paid_driver`.) Render null as a neutral
    // "plan selected after approval" state, never a fabricated "Free" (#293).
    selectedPlan: "free_driver" | "paid_driver" | null;
    certifiedLicensed: boolean;
    certifiedInsured: boolean;
    certifiedAuthorized: boolean;
    termsAcceptedAt: string;
  } | null;
  submittedAt: string;
  applicationCount: number;
  latestRejectionReason?: string | null;
  emailVerified: boolean;
  phoneVerified: boolean;
}

// #1076: an entry in the async audit queue — a solo driver who was
// auto-approved on verification and is awaiting a human admin's first look.
// Distinct shape from DriverRequest: these drivers are already
// driverApprovalStatus:"approved" (auto-approved, not pending).
export interface DriverAuditRequest {
  id: string;
  email: string | null;
  name: string;
  phone: string | null;
  driverApprovalStatus: string;
  driverApprovalSource: "manual" | "auto";
  driverAuditStatus: "unreviewed" | "reviewed";
  driverRegistrationInfo: DriverRequest["driverRegistrationInfo"];
  approvedAt: string | null;
  driverSuspended: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
  submittedAt: string;
}

// Driver approval history entry type
export interface DriverApprovalHistoryEntry {
  id: string;
  action: "submitted" | "approved" | "auto_approved" | "rejected" | "reapplied" | "audit_reviewed";
  rejectionReason: string | null;
  notes: string | null;
  selectedPlan: string | null;
  driverInfoSnapshot: {
    selectedPlan?: string;
    certifiedLicensed?: boolean;
    certifiedInsured?: boolean;
    certifiedAuthorized?: boolean;
    termsAcceptedAt?: string;
  } | null;
  createdAt: string;
  admin: {
    id: string;
    name: string;
    email: string;
  } | null;
}

// User type
export interface User {
  id: string;
  email: string | null;
  name: string;
  phone: string | null;
  emailVerified: boolean;
  phoneVerified: boolean;
  isActive: boolean;
  roles: string[];
  companyCount: number;
  createdAt: string;
}

// Company type
export interface Company {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  subscriptionTierId: string | null;
  subscriptionStatus: string | null;
  memberCount: number;
  reservationCount: number;
  createdAt: string;
}

// Approved driver type (for drivers list)
export interface ApprovedDriver {
  id: string;
  email: string | null;
  name: string;
  phone: string | null;
  driverApprovalStatus: string;
  driverApprovedAt: string | null;
  driverRegistrationInfo: {
    selectedPlan: "free_driver" | "paid_driver" | null;
    certifiedLicensed: boolean;
    certifiedInsured: boolean;
    certifiedAuthorized: boolean;
    termsAcceptedAt: string;
  } | null;
  companies: {
    id: string;
    name: string;
    slug: string;
    subscriptionTier: string | null;
    roles: string[];
  }[];
  reservationCount: number;
}

// Driver detail type (for driver detail page)
export interface DriverDetail {
  id: string;
  email: string | null;
  name: string;
  phone: string | null;
  /** The headshot customers see, or null when there is none. */
  profilePhotoUrl: string | null;
  isActive: boolean;
  emailVerified: boolean;
  driverApprovalStatus: string;
  driverApprovalNotes: string | null;
  driverApprovedAt: string | null;
  driverRejectedAt: string | null;
  driverRejectionReason: string | null;
  driverSuspended: boolean;
  driverSuspendedAt: string | null;
  driverSuspensionReason: string | null;
  driverRegistrationInfo: {
    selectedPlan: "free_driver" | "paid_driver" | null;
    certifiedLicensed: boolean;
    certifiedInsured: boolean;
    certifiedAuthorized: boolean;
    termsAcceptedAt: string;
  } | null;
  companies: {
    id: string;
    name: string;
    slug: string;
    subscriptionTier: string | null;
    roles: string[];
    isActive: boolean;
    joinedAt: string;
  }[];
  reservationCount: number;
  approvalHistory: DriverApprovalHistoryEntry[];
  createdAt: string;
  updatedAt: string;
}

// Driver ride type (for ride history)
export interface DriverRide {
  id: string;
  pickupAddress: string;
  dropoffAddress: string | null;
  pickupDatetime: string;
  status: string;
  totalCost: number | null;
  company: {
    id: string;
    name: string;
    slug: string;
  };
  customer: {
    id: string;
    name: string;
  } | null;
}

// Company detail type (for business detail page)
export interface CompanyDetail {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  phone: string | null;
  // `Company.address` is a Prisma `Json?` column. The registration → approval
  // flow stores a STRUCTURED `Address` object verbatim; seed/legacy rows may
  // hold a plain string. Format via `formatCompanyAddress` before rendering.
  address: string | Address | null;
  timezone: string;
  // Account lifecycle status (active|suspended) — drives the Suspend/Reinstate
  // control (#349 U3).
  status: string;
  subscriptionTierId: string | null;
  subscriptionStatus: string | null;
  subscriptionStartDate: string | null;
  subscriptionEndDate: string | null;
  // Entitlement-source epic #1388.
  trialEndsAt: string | null;
  /**
   * The SINGLE authoritative resolved customer-facing lifecycle state —
   * "trialing" | "pending_payment" | "active" | "past_due" | "ended". NOT
   * re-derivable from `subscriptionStatus` alone (a "trialing" company's
   * trial may already be expired). Drives the admin state badge/CTA.
   */
  entitlementState: string;
  /** Which of the three permitted sources backs `entitlementState`. */
  entitlementSource: string;
  /** The company's currently-active complimentary grant, if any. */
  complimentaryGrant: {
    reason: string;
    expiresAt: string;
    grantedByAdminId: string;
  } | null;
  /**
   * #1817 (ADR-021): the company's single distinguished owner (`null` for a
   * not-yet-backfilled legacy row). Compare each `members[].userId` against
   * this to derive the Owner badge — there is deliberately no separate
   * per-member `isOwner` flag on the platform payload.
   */
  ownerUserId: string | null;
  members: {
    userId: string;
    name: string;
    email: string | null;
    roles: string[];
    isActive: boolean;
    joinedAt: string;
  }[];
  reservationCount: number;
  createdAt: string;
  updatedAt: string;
}

// User detail type (for user detail page)
export interface UserDetail {
  id: string;
  email: string | null;
  name: string;
  phone: string | null;
  /** The headshot customers see, or null when there is none. */
  profilePhotoUrl: string | null;
  emailVerified: boolean;
  phoneVerified: boolean;
  isActive: boolean;
  // Driver approval (for solo driver applications)
  driverApprovalStatus: string | null;
  driverApprovalNotes: string | null;
  driverApprovedAt: string | null;
  driverRejectedAt: string | null;
  driverRejectionReason: string | null;
  // Business approval (for company registrations)
  businessApprovalStatus: string | null;
  businessApprovalNotes: string | null;
  businessApprovedAt: string | null;
  businessRejectedAt: string | null;
  businessRejectionReason: string | null;
  driverRegistrationInfo: {
    selectedPlan: "free_driver" | "paid_driver" | null;
    certifiedLicensed: boolean;
    certifiedInsured: boolean;
    certifiedAuthorized: boolean;
    termsAcceptedAt: string;
  } | null;
  companyRegistrationInfo: {
    companyName: string;
    companyEmail: string;
    companyPhone: string | null;
    selectedPlan: string;
    fleetSize: string;
  } | null;
  memberships: {
    companyId: string;
    companyName: string;
    companySlug: string;
    subscriptionTier: string | null;
    roles: string[];
    isActive: boolean;
    joinedAt: string;
  }[];
  createdAt: string;
  updatedAt: string;
}

// Company request type
export interface CompanyRequest {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  businessApprovalStatus: string;
  companyName: string | null;
  fleetSize: string | null;
  /** Auto-generated suggested slug based on company name */
  suggestedSlug: string | null;
  /**
   * The company subscription tier the applicant chose at registration
   * (`business` / `business_plus` / `business_ultimate`), or null for older
   * pending rows registered before the plan was captured. The approve modal
   * defaults its tier selector to this value (#297).
   */
  selectedPlan: string | null;
  submittedAt: string;
}

// Platform stats type
export interface PlatformStats {
  totalUsers: number;
  pendingDrivers: number; // Email- or phone-verified drivers ready for review
  pendingDriversAwaitingVerification: number; // Drivers who still need email or phone verification
  pendingCompanies: number;
  approvedDrivers: number;
  totalCompanies: number;
  totalReservations: number;
}

export interface PlatformHealthCheck {
  name: string;
  status: "healthy" | "degraded";
}

export interface PlatformHealth {
  status: "healthy" | "degraded";
  checkedAt: string;
  checks: PlatformHealthCheck[];
}

export type MeteredProvider = "mapbox" | "airlabs";

export interface ProviderUsageProviderTotal {
  provider: MeteredProvider;
  /** Exact decimal string; do not coerce through JavaScript Number. */
  allocatedCalls: string;
}

export type MapboxClientSessionPlatform = "web" | "native";

export interface ProviderUsageMapboxClientSessionTotal {
  platform: MapboxClientSessionPlatform;
  /** Aggregate observed lifecycle count; keep as a string for lossless display. */
  observedSessions: string;
}

export interface ProviderUsageCompanyRollup {
  companyId: string;
  companyName: string;
  providerUsage: ProviderUsageProviderTotal[];
  mapboxClientSessions: ProviderUsageMapboxClientSessionTotal[];
  /** Exact decimal string; do not coerce through JavaScript Number. */
  totalAllocatedCalls: string;
}

export interface ProviderUsageMonthlyRollup {
  month: string;
  periodStart: string;
  periodEndExclusive: string;
  /** Platform totals include the separately reported unattributed bucket. */
  providerTotals: ProviderUsageProviderTotal[];
  unattributedTotals: ProviderUsageProviderTotal[];
  mapboxClientSessions: {
    totals: ProviderUsageMapboxClientSessionTotal[];
    unattributedTotals: ProviderUsageMapboxClientSessionTotal[];
  };
  companies: ProviderUsageCompanyRollup[];
  topConsumers: Pick<
    ProviderUsageCompanyRollup,
    "companyId" | "companyName" | "totalAllocatedCalls"
  >[];
}

// Sidebar count item type
export interface SidebarCountItem {
  total: number;
  new: number;
}

// Sidebar counts type
export interface SidebarCounts {
  users: SidebarCountItem;
  drivers: SidebarCountItem;
  driverRequests: SidebarCountItem;
  businesses: SidebarCountItem;
  businessRequests: SidebarCountItem;
  inquiries: SidebarCountItem;
}

// Last viewed timestamps for sidebar pages
export interface LastViewedTimestamps {
  usersLastViewed?: string;
  driversLastViewed?: string;
  driverRequestsLastViewed?: string;
  businessesLastViewed?: string;
  businessRequestsLastViewed?: string;
  inquiriesLastViewed?: string;
}

// API functions
/**
 * Fetches the current platform admin session.
 * @returns Platform admin data
 */
export async function getPlatformSession(): Promise<PlatformAdmin> {
  const { admin } = await apiGet<{ admin: PlatformAdmin }>("/auth/session");
  return admin;
}

/**
 * Fetches platform statistics.
 * @returns Platform statistics
 */
export async function getPlatformStats(): Promise<PlatformStats> {
  return apiGet<PlatformStats>("/stats");
}

/**
 * Fetches aggregate platform health for the admin dashboard.
 * @returns Sanitized platform health status and aggregate checks
 */
export async function getPlatformHealth(): Promise<PlatformHealth> {
  return apiGet<PlatformHealth>("/health");
}

/**
 * Fetches exact provider-attempt allocation plus separate observed map sessions.
 * @param month - UTC month in YYYY-MM form
 * @returns Exact attempts, observed sessions, unattributed totals, and company rollups
 */
export async function getPlatformProviderUsage(
  month: string
): Promise<ProviderUsageMonthlyRollup> {
  return apiGet<ProviderUsageMonthlyRollup>(
    `/provider-usage?month=${encodeURIComponent(month)}`
  );
}

/**
 * Fetches sidebar counts with optional "new since" filtering.
 * @param lastViewed - Object with last viewed timestamps for each page
 * @returns Sidebar counts with total and new counts for each section
 */
export async function getSidebarCounts(lastViewed?: LastViewedTimestamps): Promise<SidebarCounts> {
  const params = new URLSearchParams();
  if (lastViewed?.usersLastViewed) params.append("usersLastViewed", lastViewed.usersLastViewed);
  if (lastViewed?.driversLastViewed)
    params.append("driversLastViewed", lastViewed.driversLastViewed);
  if (lastViewed?.driverRequestsLastViewed)
    params.append("driverRequestsLastViewed", lastViewed.driverRequestsLastViewed);
  if (lastViewed?.businessesLastViewed)
    params.append("businessesLastViewed", lastViewed.businessesLastViewed);
  if (lastViewed?.businessRequestsLastViewed)
    params.append("businessRequestsLastViewed", lastViewed.businessRequestsLastViewed);
  if (lastViewed?.inquiriesLastViewed)
    params.append("inquiriesLastViewed", lastViewed.inquiriesLastViewed);

  const queryString = params.toString();
  return apiGet<SidebarCounts>(`/sidebar-counts${queryString ? `?${queryString}` : ""}`);
}

/**
 * Fetches paginated driver requests.
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 20)
 * @param search - Optional search filter (name or email, #564)
 * @returns Paginated driver requests
 */
export async function getDriverRequests(
  page = 1,
  limit = 20,
  search?: string
): Promise<PaginatedResponse<DriverRequest>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) params.append("search", search);
  return apiGet<PaginatedResponse<DriverRequest>>(`/driver-requests?${params.toString()}`);
}

/**
 * Fetches a single driver request by ID.
 * @param id - Driver request ID
 * @returns Driver request data
 */
export async function getDriverRequest(id: string): Promise<DriverRequest> {
  return apiGet<DriverRequest>(`/driver-requests/${id}`);
}

/**
 * Approves a driver request.
 * @param id - Driver request ID
 * @param notes - Optional approval notes
 */
export async function approveDriver(id: string, notes?: string): Promise<void> {
  await api.put(`/driver-requests/${id}/approve`, { notes });
}

/**
 * Rejects a driver request.
 * @param id - Driver request ID
 * @param reason - Optional rejection reason code (internal use only)
 * @param notes - Optional additional notes
 */
export async function rejectDriver(id: string, reason?: string, notes?: string): Promise<void> {
  await api.put(`/driver-requests/${id}/reject`, { reason, notes });
}

/**
 * Fetches driver approval history.
 * @param id - Driver/user ID
 * @returns Array of history entries
 */
export async function getDriverHistory(id: string): Promise<DriverApprovalHistoryEntry[]> {
  return apiGet<DriverApprovalHistoryEntry[]>(`/driver-requests/${id}/history`);
}

/**
 * Fetches the async audit queue (#1076): solo drivers auto-approved on
 * verification, awaiting a human admin's first look. Distinct from
 * {@link getDriverRequests} — these drivers are already `driverApprovalStatus:
 * "approved"`, so they never show in the pending-applicant queue.
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 20)
 * @returns Paginated audit-queue entries
 */
export async function getDriverAuditQueue(
  page = 1,
  limit = 20
): Promise<PaginatedResponse<DriverAuditRequest>> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    audit: "unreviewed",
  });
  return apiGet<PaginatedResponse<DriverAuditRequest>>(`/driver-requests?${params.toString()}`);
}

/**
 * Marks an auto-approved driver's async audit as reviewed (#1076). Pure
 * record-keeping — it does NOT suspend or otherwise change the driver's
 * access; use {@link suspendDriver} for that (reachable from the driver's
 * detail page).
 * @param id - Driver/user ID
 */
export async function markDriverAuditReviewed(id: string): Promise<void> {
  await api.put(`/driver-requests/${id}/audit-reviewed`, {});
}

/**
 * A requested column sort (#807 — sortable table columns). `sortBy` is
 * validated server-side against a per-endpoint allowlist; an unrecognized
 * value is silently ignored (falls back to the endpoint's default order).
 */
export interface SortParam {
  sortBy: string;
  sortOrder: "asc" | "desc";
}

/**
 * Append an optional {@link SortParam} to a `URLSearchParams` in place.
 * @param params - The URLSearchParams to mutate.
 * @param sort - The requested sort, or undefined to leave `params` unchanged.
 */
function appendSort(params: URLSearchParams, sort?: SortParam): void {
  if (!sort) return;
  params.append("sortBy", sort.sortBy);
  params.append("sortOrder", sort.sortOrder);
}

/**
 * Fetches paginated users.
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 20)
 * @param search - Search filter
 * @param role - Role filter (admin, manager, driver, customer)
 * @param verified - Verification status filter (true, false, or undefined for all)
 * @param sort - Optional column sort (name/email/createdAt)
 * @returns Paginated users
 */
export async function getUsers(
  page = 1,
  limit = 20,
  search?: string,
  role?: string,
  verified?: boolean,
  sort?: SortParam
): Promise<PaginatedResponse<User>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) params.append("search", search);
  if (role) params.append("role", role);
  if (verified !== undefined) params.append("verified", String(verified));
  appendSort(params, sort);

  return apiGet<PaginatedResponse<User>>(`/users?${params.toString()}`);
}

/**
 * Fetches a single user by ID.
 * @param id - User ID
 * @returns User data
 */
export async function getUser(id: string): Promise<User> {
  return apiGet<User>(`/users/${id}`);
}

/**
 * Updates a user.
 * @param id - User ID
 * @param data - Update data
 * @param data.name - User name
 * @param data.isActive - Whether user is active
 */
export async function updateUser(
  id: string,
  data: { name?: string; isActive?: boolean }
): Promise<void> {
  await api.put(`/users/${id}`, data);
}

/**
 * Result of an admin-initiated password reset (deprovisioning epic #349 U6).
 * `emailDisabled` is `true` when email sending is off for the environment, in
 * which case no reset email was sent — the UI must surface that honestly.
 */
export interface PasswordResetResult {
  success: boolean;
  emailDisabled?: boolean;
  message?: string;
}

/**
 * Trigger an admin-initiated password reset for a user (deprovisioning epic
 * #349 U6). The API mints a Supabase recovery link server-side and emails it to
 * the user; the link is never returned here. When email is disabled, the result
 * carries `emailDisabled: true`.
 * @param id - User ID
 * @returns The reset result (whether an email was actually sent)
 */
export async function requestUserPasswordReset(id: string): Promise<PasswordResetResult> {
  const response = await api.post<PasswordResetResult>(`/users/${id}/password-reset`);
  return response.data;
}

/**
 * Fetches paginated companies.
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 20)
 * @param search - Search filter
 * @param excludeSoloDrivers - Exclude solo driver companies (free_driver/paid_driver tiers)
 * @param sort - Optional column sort (name/createdAt)
 * @returns Paginated companies
 */
export async function getCompanies(
  page = 1,
  limit = 20,
  search?: string,
  excludeSoloDrivers?: boolean,
  sort?: SortParam
): Promise<PaginatedResponse<Company>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) params.append("search", search);
  if (excludeSoloDrivers) params.append("excludeSoloDrivers", "true");
  appendSort(params, sort);

  return apiGet<PaginatedResponse<Company>>(`/companies?${params.toString()}`);
}

/**
 * Fetches a single company by ID with full details.
 * @param id - Company ID
 * @returns Company detail data including members
 */
export async function getCompanyDetail(id: string): Promise<CompanyDetail> {
  return apiGet<CompanyDetail>(`/companies/${id}`);
}

/**
 * Fetches paginated approved drivers.
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 20)
 * @param search - Search filter (name or email)
 * @param sort - Optional column sort (name/driverApprovedAt)
 * @returns Paginated approved drivers
 */
export async function getDrivers(
  page = 1,
  limit = 20,
  search?: string,
  sort?: SortParam
): Promise<PaginatedResponse<ApprovedDriver>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) params.append("search", search);
  appendSort(params, sort);

  return apiGet<PaginatedResponse<ApprovedDriver>>(`/drivers?${params.toString()}`);
}

/**
 * Fetches a single driver by ID with full details.
 * @param id - Driver ID
 * @returns Driver detail data including companies and approval history
 */
export async function getDriverDetail(id: string): Promise<DriverDetail> {
  return apiGet<DriverDetail>(`/drivers/${id}`);
}

/**
 * Fetches paginated ride history for a driver.
 * @param id - Driver ID
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 20)
 * @returns Paginated driver rides
 */
export async function getDriverRides(
  id: string,
  page = 1,
  limit = 20
): Promise<PaginatedResponse<DriverRide>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });

  return apiGet<PaginatedResponse<DriverRide>>(`/drivers/${id}/rides?${params.toString()}`);
}

/**
 * Reversibly suspend an approved driver (deprovisioning epic #349 U2). Records a
 * reason; the API audits it (server-derived actor) and notifies the driver.
 * @param id - Driver/user ID
 * @param reason - Why the admin is suspending (min 10 chars)
 */
export async function suspendDriver(id: string, reason: string): Promise<void> {
  await api.put(`/drivers/${id}/suspend`, { reason });
}

/**
 * Lift a driver's suspension (deprovisioning epic #349 U2).
 * @param id - Driver/user ID
 * @param notes - Optional reinstatement notes
 */
export async function reinstateDriver(id: string, notes?: string): Promise<void> {
  await api.put(`/drivers/${id}/reinstate`, notes ? { notes } : {});
}

/**
 * Fetches a single user by ID with full details.
 * @param id - User ID
 * @returns User detail data including memberships
 */
export async function getUserDetail(id: string): Promise<UserDetail> {
  return apiGet<UserDetail>(`/users/${id}`);
}

/**
 * Fetches paginated company registration requests.
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 20)
 * @param search - Optional search filter (name or email, #564)
 * @returns Paginated company requests
 */
export async function getCompanyRequests(
  page = 1,
  limit = 20,
  search?: string
): Promise<PaginatedResponse<CompanyRequest>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) params.append("search", search);
  return apiGet<PaginatedResponse<CompanyRequest>>(`/company-requests?${params.toString()}`);
}

/**
 * Fetches a single company request by ID.
 * @param id - Company request ID
 * @returns Company request data
 */
export async function getCompanyRequest(id: string): Promise<CompanyRequest> {
  return apiGet<CompanyRequest>(`/company-requests/${id}`);
}

/**
 * Approves a company registration request.
 * @param id - Company request ID
 * @param options - Approval options
 * @param options.notes - Optional approval notes
 * @param options.subscriptionTierId - Subscription tier to assign (default: business)
 * @param options.slug - Custom slug for the company URL (optional - auto-generated if not provided)
 */
export async function approveCompanyRequest(
  id: string,
  options?: {
    notes?: string;
    subscriptionTierId?: string;
    /** Custom slug for the company URL (optional - auto-generated if not provided) */
    slug?: string;
  }
): Promise<void> {
  await api.put(`/company-requests/${id}/approve`, {
    notes: options?.notes,
    subscriptionTierId: options?.subscriptionTierId ?? "business",
    slug: options?.slug,
  });
}

/**
 * Rejects a company registration request.
 * @param id - Company request ID
 * @param notes - Rejection reason
 */
export async function rejectCompanyRequest(id: string, notes: string): Promise<void> {
  await api.put(`/company-requests/${id}/reject`, { notes });
}

/**
 * Updates a company's slug (URL).
 * WARNING: This is a dangerous operation that will break existing booking links.
 * @param companyId - Company ID
 * @param slug - New slug
 * @returns Updated company info with previous slug
 */
export async function updateCompanySlug(
  companyId: string,
  slug: string
): Promise<{ id: string; name: string; slug: string; previousSlug: string }> {
  const response = await api.put<
    ApiResponse<{ id: string; name: string; slug: string; previousSlug: string }>
  >(`/companies/${companyId}/slug`, { slug });
  return response.data.data;
}

/**
 * Suspend or reinstate a company (deprovisioning #349 U3). Records a reason
 * (audit log) and notifies the company's members via the platform service.
 * @param companyId - Company ID
 * @param status - Target status ("active" to reinstate, "suspended" to suspend)
 * @param reason - Why the admin is changing the status (min 10 chars)
 * @returns The updated company summary
 */
export async function setCompanyStatus(
  companyId: string,
  status: "active" | "suspended",
  reason: string
): Promise<{ id: string; name: string; status: string }> {
  const response = await api.put<ApiResponse<{ id: string; name: string; status: string }>>(
    `/companies/${companyId}/status`,
    { status, reason }
  );
  return response.data.data;
}

/**
 * Deactivate or reactivate a company member (deprovisioning #349 U4). Audits
 * the change and notifies the affected user via the platform service.
 * @param companyId - Company ID
 * @param userId - The target member's user ID
 * @param isActive - Desired active state (false = deactivate, true = reactivate)
 * @returns The updated membership summary
 */
export async function setCompanyMemberStatus(
  companyId: string,
  userId: string,
  isActive: boolean
): Promise<{ userId: string; isActive: boolean }> {
  const response = await api.put<ApiResponse<{ userId: string; isActive: boolean }>>(
    `/companies/${companyId}/members/${userId}/status`,
    { isActive }
  );
  return response.data.data;
}

/**
 * Cancel a company's Stripe subscription (deprovisioning #349 U5). By default
 * cancels at the end of the paid period (the company keeps access until then);
 * `immediate: true` hard-cancels now (abuse cases). Records a reason (audit log)
 * and notifies the company. The Stripe webhook reconciles the local
 * subscription status/tier.
 * @param companyId - Company ID
 * @param params - Cancel options.
 * @param params.immediate - Hard-cancel now (default false = cancel at period end)
 * @param params.reason - Why the admin is canceling (min 10 chars)
 * @returns A summary of the cancellation request
 */
export async function cancelCompanySubscription(
  companyId: string,
  params: { immediate: boolean; reason: string }
): Promise<{ companyId: string; immediate: boolean; subscriptionStatus: string }> {
  const response = await api.put<
    ApiResponse<{ companyId: string; immediate: boolean; subscriptionStatus: string }>
  >(`/companies/${companyId}/subscription/cancel`, params);
  return response.data.data;
}

/**
 * Grant a company audited complimentary (comp) paid-tier access
 * (entitlement-source epic #1388) — one of the three permitted paid-
 * entitlement sources. Always requires a reason and a future expiry (no
 * indefinite/un-audited comp state).
 * @param companyId - Company ID.
 * @param params - Grant options.
 * @param params.reason - Why the admin is granting free access (min 10 chars).
 * @param params.expiresAt - ISO datetime the grant expires (must be in the future).
 * @returns A summary of the created grant.
 */
export async function grantCompanyComplimentaryAccess(
  companyId: string,
  params: { reason: string; expiresAt: string }
): Promise<{ id: string; companyId: string; reason: string; expiresAt: string }> {
  const response = await api.put<
    ApiResponse<{ id: string; companyId: string; reason: string; expiresAt: string }>
  >(`/companies/${companyId}/complimentary-grant`, params);
  return response.data.data;
}

/**
 * Revoke a company's currently-active complimentary grant early (#1388).
 * @param companyId - Company ID.
 * @returns A summary of the revocation.
 */
export async function revokeCompanyComplimentaryAccess(
  companyId: string
): Promise<{ companyId: string; revoked: boolean }> {
  const response = await api.delete<ApiResponse<{ companyId: string; revoked: boolean }>>(
    `/companies/${companyId}/complimentary-grant`
  );
  return response.data.data;
}

/** A grant row's derived roster status (#1553) — distinct from a company's
 * resolved `entitlementState`; this describes the GRANT ROW itself. */
export type ComplimentaryGrantStatus = "active" | "expiring_soon" | "expired" | "revoked";

/** One row of the platform-wide complimentary-grants roster (#1553). */
export interface ComplimentaryGrantEntry {
  id: string;
  companyId: string;
  companyName: string;
  companySlug: string;
  grantedByAdminId: string;
  grantedByAdminName: string;
  grantedByAdminEmail: string;
  reason: string;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  revokedByAdminId: string | null;
  revokedByAdminName: string | null;
  status: ComplimentaryGrantStatus;
}

/** The complimentary-grants roster response — the standard paginated
 * envelope PLUS `liveCount`, an unconditional platform-wide count of
 * currently-active grants (independent of the request's own status
 * filter/search/page) for the roster page's "N live grants" header stat. */
export interface ComplimentaryGrantsPage extends PaginatedResponse<ComplimentaryGrantEntry> {
  liveCount: number;
}

/**
 * Fetches the platform-wide, read-only complimentary-grants roster (#1553).
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 20)
 * @param status - Optional status filter (active/expiring_soon/expired/revoked); omit for every status
 * @param search - Optional free-text search (company name or granting-admin name/email)
 * @returns Paginated complimentary grants + the unconditional live-grant count
 */
export async function getComplimentaryGrants(
  page = 1,
  limit = 20,
  status?: ComplimentaryGrantStatus,
  search?: string
): Promise<ComplimentaryGrantsPage> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (status) params.append("status", status);
  if (search) params.append("search", search);

  return apiGet<ComplimentaryGrantsPage>(`/complimentary-grants?${params.toString()}`);
}

// Inquiry type
export interface Inquiry {
  id: string;
  name: string;
  email: string;
  /** Null when captured via the short homepage form (not collected there). */
  phone: string | null;
  companyName: string;
  desiredPlan: string;
  fleetSize: string | null;
  /** Null when captured via the short homepage form (not collected there). */
  country: string | null;
  /**
   * Lead-capture entry point for attribution: 'registration_inquiry' (the full
   * Business Inquiry form) or 'homepage_demo' (the short homepage form).
   */
  source: string;
  status: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * The pending company-registration user's id, once this lead has actually
   * been converted (#277) — the source of truth for "has a real record been
   * created", distinct from `status` (which could be "converted" cosmetically
   * with no backing record for rows converted before #277 shipped).
   */
  convertedUserId: string | null;
  convertedAt: string | null;
}

/** Body for {@link convertInquiry} — pre-filled from the inquiry, admin-editable. */
export interface ConvertInquiryInput {
  name: string;
  email: string;
  phone: string;
  companyName: string;
  desiredPlan: string;
  fleetSize?: string;
  country: string;
}

// Inquiry stats type
export interface InquiryStats {
  new: number;
  contacted: number;
  converted: number;
  closed: number;
  total: number;
}

/**
 * Fetches paginated inquiries.
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 20)
 * @param status - Status filter
 * @param search - Optional search filter (name, email, or company name, #564)
 * @returns Paginated inquiries
 */
export async function getInquiries(
  page = 1,
  limit = 20,
  status?: string,
  search?: string
): Promise<PaginatedResponse<Inquiry>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (status) params.append("status", status);
  if (search) params.append("search", search);

  return apiGet<PaginatedResponse<Inquiry>>(`/inquiries?${params.toString()}`);
}

/**
 * Fetches inquiry stats.
 * @returns Inquiry status counts
 */
export async function getInquiryStats(): Promise<InquiryStats> {
  return apiGet<InquiryStats>("/inquiries/stats");
}

/**
 * Fetches a single inquiry by ID.
 * @param id - Inquiry ID
 * @returns Inquiry data
 */
export async function getInquiry(id: string): Promise<Inquiry> {
  return apiGet<Inquiry>(`/inquiries/${id}`);
}

/**
 * Updates an inquiry.
 * @param id - Inquiry ID
 * @param data - Update data
 * @param data.status - Inquiry status
 * @param data.notes - Inquiry notes
 */
export async function updateInquiry(
  id: string,
  data: { status?: string; notes?: string }
): Promise<void> {
  await api.put(`/inquiries/${id}`, data);
}

/**
 * Soft-deletes an inquiry (recoverable via {@link restoreInquiry}).
 * @param id - Inquiry ID
 */
export async function deleteInquiry(id: string): Promise<void> {
  await api.delete(`/inquiries/${id}`);
}

/**
 * Restores a previously soft-deleted inquiry (undo).
 * @param id - Inquiry ID
 * @returns The restored inquiry
 */
export async function restoreInquiry(id: string): Promise<Inquiry> {
  const response = await api.post<ApiResponse<Inquiry>>(`/inquiries/${id}/restore`);
  return response.data.data;
}

/** The response shape from {@link convertInquiry}. */
export interface ConvertInquiryResult {
  inquiry: Inquiry;
  convertedUserId: string;
  emailSent: boolean;
}

/**
 * Converts a lead into a pending company-registration handoff (#277).
 * Idempotent: converting an already-converted inquiry 409s — callers should
 * read `error.response.data.error.details.convertedUserId` in that case.
 * @param id - Inquiry ID
 * @param data - The (pre-filled, admin-editable) lead details to register with
 * @returns The updated inquiry, the new pending user's id, and whether the
 *   claim email was sent
 */
export async function convertInquiry(
  id: string,
  data: ConvertInquiryInput
): Promise<ConvertInquiryResult> {
  const response = await api.post<ApiResponse<ConvertInquiryResult>>(
    `/inquiries/${id}/convert`,
    data
  );
  return response.data.data;
}

// ============================================================================
// Subscription Tier Editor
// ============================================================================

export type EditableTierLimit = Exclude<TierLimit, "maxReservationsMonth">;
export type EditableTierRate = Exclude<TierRate, "overageReservationPriceCents">;

export type TierUpdatePayload = Partial<{
  displayName: string;
  description: string | null;
  monthlyPrice: number;
  isActive: boolean;
  sortOrder: number;
}> &
  Partial<Record<EditableTierLimit, number | null>> &
  Partial<Record<TierFeatureFlag, boolean>> &
  Partial<Record<EditableTierRate, number>>;

export interface TierListResponse {
  tiers: SubscriptionTier[];
  schema: {
    flags: readonly TierFeatureFlag[];
    limits: readonly TierLimit[];
    rates: readonly TierRate[];
  };
}

/**
 * Fetches every subscription tier with the editable schema sidecar.
 * @returns Tier rows + the list of editable column names per category.
 */
export async function getTiers(): Promise<TierListResponse> {
  return apiGet<TierListResponse>("/tiers");
}

/**
 * Patches a subscription tier with any subset of editable columns.
 * The API invalidates its local feature-gate cache after the write. Another
 * API instance can retain prior gate values until the short cache TTL expires.
 * Published reservation allowances and overage rates are not editable here;
 * active usage segments keep the terms captured when the segment opened.
 * @param id - Tier id (e.g. "business_plus")
 * @param payload - Columns to update; omit any column you're not changing.
 * @returns The updated tier row.
 */
export async function updateTier(
  id: string,
  payload: TierUpdatePayload
): Promise<SubscriptionTier> {
  const response = await api.put<ApiResponse<SubscriptionTier>>(`/tiers/${id}`, payload);
  return response.data.data;
}

/** Usage stats for a tier (the blast-radius warning in the save dialog). */
export interface TierUsage {
  /** Number of companies currently on this tier. */
  companyCount: number;
}

/**
 * Fetches how many companies are currently on a tier so the save dialog can
 * warn about an editable change's company-level blast radius. The locked
 * reservation allowance and overage fields never appear in that dialog.
 * @param id - Tier id (e.g. "business_plus").
 * @returns The tier's usage stats.
 */
export async function getTierUsage(id: string): Promise<TierUsage> {
  return apiGet<TierUsage>(`/tiers/${id}/usage`);
}

// ============================================================================
// Reservation oversight
// ============================================================================

export interface AdminReservationVehicle {
  id: string;
  label: string;
  source: "owned" | "viaDriver";
  /** The photo customers see once this car is assigned; null when none is set. */
  photoUrl: string | null;
}

/**
 * Removes a vehicle photo that violates the terms.
 *
 * `photoUrl` is the photo the admin was actually shown. The server
 * compare-and-swaps on it, so if the owner replaced the picture since the page
 * loaded the request fails instead of destroying a replacement nobody has
 * complained about.
 * @param vehicleId - The vehicle whose photo is being removed
 * @param photoUrl - The exact photo URL rendered to the admin
 */
export async function deleteVehiclePhotoAsAdmin(
  vehicleId: string,
  photoUrl: string
): Promise<void> {
  await api.delete(`/vehicles/${vehicleId}/photo`, { data: { photoUrl } });
}

/**
 * Removes a profile headshot that violates the terms.
 *
 * Same compare-and-swap contract as the vehicle takedown: `photoUrl` is the
 * picture the admin was actually shown, so a replacement uploaded since the
 * page loaded is never destroyed by a click aimed at the old one.
 * @param userId - The person whose photo is being removed
 * @param photoUrl - The exact photo URL rendered to the admin
 */
export async function deleteProfilePhotoAsAdmin(
  userId: string,
  photoUrl: string
): Promise<void> {
  await api.delete(`/users/${userId}/profile-photo`, { data: { photoUrl } });
}

// ============================================================================
// Fleet oversight (#2585)
// ============================================================================

/**
 * One vehicle on the platform-admin oversight surface.
 *
 * Narrower than the operator-facing vehicle: the VIN and any owner identifier
 * are absent from the server projection, because no oversight task on this
 * screen needs either.
 */
export interface PlatformVehicle {
  id: string;
  companyId: string;
  /** The accountable company — the console's link target for the fleet's owner. */
  company: { id: string; name: string; slug: string | null };
  make: string;
  model: string;
  year: number | null;
  color: string | null;
  licensePlate: string | null;
  capacity: number | null;
  luggageCapacity: number | null;
  /** Canonical evidence: a recorded yes, a recorded no, or no answer at all. */
  wheelchairAccessibleStatus: VehicleCapabilityStatus;
  childSeatCapableStatus: VehicleCapabilityStatus;
  isDefault: boolean;
  /** The full-size photo customers see; the takedown decision surface. */
  photoUrl: string | null;
  /** The bounded rendition list tiles load; null for photos predating derivatives. */
  photoThumbUrl: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

/** Narrowing options for the platform-wide fleet read. */
export interface PlatformVehicleFilters {
  /** One company's fleet — how the business detail page links here. */
  companyId?: string;
  /** One car — how an audit-log row resolves to its subject. */
  vehicleId?: string;
  status?: "active" | "retired";
  /** Matches make, model or plate. */
  search?: string;
  /** Only cars currently carrying a photo (the takedown starting point). */
  hasPhoto?: boolean;
}

/**
 * Reads vehicles across every company for the platform console.
 * @param page - 1-based page number
 * @param limit - Page size (server caps at 100)
 * @param filters - Optional narrowing
 * @returns The paginated fleet page
 */
export async function getPlatformVehicles(
  page = 1,
  limit = 20,
  filters: PlatformVehicleFilters = {}
): Promise<PaginatedResponse<PlatformVehicle>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (filters.companyId) params.append("companyId", filters.companyId);
  if (filters.vehicleId) params.append("vehicleId", filters.vehicleId);
  if (filters.status) params.append("status", filters.status);
  if (filters.search) params.append("search", filters.search);
  if (filters.hasPhoto) params.append("hasPhoto", "true");
  return apiGet<PaginatedResponse<PlatformVehicle>>(`/vehicles?${params.toString()}`);
}

export interface AdminReservationListItem {
  id: string;
  status: string;
  driverAcceptedAt: string | null;
  pickupDatetime: string;
  /** IANA zone of the pickup; render pickupDatetime in this zone, not the viewer's (#313). */
  pickupTimezone: string;
  pickupLocation: string;
  dropoffLocation: string;
  guestName: string;
  totalCost: number | null;
  company: { id: string; name: string };
  driver: { id: string; name: string } | null;
  vehicle: AdminReservationVehicle | null;
  createdAt: string;
}

export interface AdminReservationDetail {
  id: string;
  status: string;
  driverAcceptedAt: string | null;
  driverDeclineReason: string | null;
  cancellationReason: string | null;
  pickupDatetime: string;
  pickupTimezone: string;
  pickupLocation: string;
  dropoffLocation: string;
  passengerCount: number;
  luggageCount: number;
  specialNotes: string | null;
  guestFirstName: string;
  guestLastName: string;
  guestEmail: string;
  guestPhone: string;
  totalCost: number | null;
  paymentStatus: string;
  paymentMethod: string | null;
  amountPaid: number | null;
  refundedAmount: number | null;
  refundedAt: string | null;
  company: { id: string; name: string; email: string | null; phone: string | null };
  driver: { id: string; name: string; email: string | null; phone: string | null } | null;
  vehicle: AdminReservationVehicle | null;
  rideType: { id: string; name: string; type: string } | null;
  serviceLevel: { id: string; name: string } | null;
  statusHistory: {
    id: string;
    action: string;
    fromStatus: string | null;
    toStatus: string | null;
    notes: string | null;
    createdAt: string;
  }[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Platform reservation list response: the standard paginated envelope plus
 * per-status counts (#872) computed server-side via a single grouped query,
 * scoped to the current search/date/company filters but NOT the active
 * status filter itself — so every pill's badge stays meaningful regardless
 * of which pill is currently selected.
 */
export interface AdminReservationListResponse extends PaginatedResponse<AdminReservationListItem> {
  statusCounts: Record<string, number>;
}

/** Live PaymentIntent probe for platform reservation payment visibility (#985). */
export interface AdminReservationPaymentStatus {
  pending: boolean;
  status: string | null;
  amount: number | null;
}

/** Platform-admin refund request body; server revalidates all bounds. */
export interface AdminReservationRefundRequest {
  amount: number;
  confirmationAmount: number;
  reason: string;
  idempotencyKey: string;
  resumeRequestId?: string;
}

/** Platform-admin refund response body after ReservationService.refund succeeds. */
export interface AdminReservationRefundResult {
  id: string;
  refundedAmount: number | null;
  paymentStatus: string;
  refundId: string;
  refundIds: string[];
  amount: number;
  reason: string;
  requestId: string;
}

export type AdminReservationMessageTarget = "guest" | "company" | "driver";

/** Platform-admin support message payload for a reservation (#835). */
export interface AdminReservationMessageRequest {
  target: AdminReservationMessageTarget;
  subject: string;
  message: string;
}

/** Platform-admin support message result. */
export interface AdminReservationMessageResult {
  target: AdminReservationMessageTarget;
  delivery: "email" | "notification_email" | "support_thread";
  supportConversationId?: string;
}

/**
 * List reservations across all companies (platform oversight).
 * @param page - 1-based page
 * @param limit - Page size
 * @param status - Optional status filter (comma-separated)
 * @param search - Optional guest name/email/phone search
 * @param fromDate - Optional pickup date lower bound (YYYY-MM-DD or ISO)
 * @param toDate - Optional pickup date upper bound (YYYY-MM-DD or ISO)
 * @param sort - Optional column sort (pickupDatetime only — the one date column shown)
 * @returns A paginated reservation list, with per-status counts (#872)
 */
export async function getReservations(
  page: number,
  limit: number,
  status?: string,
  search?: string,
  fromDate?: string,
  toDate?: string,
  sort?: SortParam
): Promise<AdminReservationListResponse> {
  const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
  if (status) params.append("status", status);
  if (search) params.append("search", search);
  if (fromDate) params.append("fromDate", fromDate);
  if (toDate) params.append("toDate", toDate);
  appendSort(params, sort);
  return apiGet(`/reservations?${params.toString()}`);
}

/**
 * Fetch a single reservation's full detail + status history.
 * @param id - Reservation id
 * @returns The reservation detail
 */
export async function getReservationDetail(id: string): Promise<AdminReservationDetail> {
  return apiGet(`/reservations/${id}`);
}

/**
 * Live-probe a reservation's stored PaymentIntent for platform-admin payment
 * visibility (#985). Read-only; never mutates reservation state.
 * @param id - Reservation id
 * @returns The pending status probe result
 */
export async function getReservationPaymentStatusAsAdmin(
  id: string
): Promise<AdminReservationPaymentStatus> {
  return apiGet(`/reservations/${id}/payment-status`);
}

/**
 * Force-cancel a reservation as a platform admin (records a reason; notifies
 * the customer + driver via the shared reservation service).
 * @param id - Reservation id
 * @param reason - Why the admin is cancelling
 */
export async function cancelReservationAsAdmin(id: string, reason: string): Promise<void> {
  await api.post(`/reservations/${id}/cancel`, { reason });
}

/**
 * The non-financial intervention subset the admin detail page drives (#315):
 * reassign/clear driver and reschedule.
 * Everything routes through `ReservationService` server-side (state machine,
 * membership/suspension checks on assignment, audit trail, notifications).
 */
export interface AdminReservationIntervention {
  /** Assign a company driver (their user id), or `null` to return the ride to the pool. */
  driverUserId?: string | null;
  /** New pickup instant (UTC ISO). Send together with pickupTimezone. */
  pickupDatetime?: string;
  /** IANA timezone the pickup time is anchored to. */
  pickupTimezone?: string;
}

/**
 * Platform-admin reservation intervention (#315): PUT any subset of
 * {@link AdminReservationIntervention} to `/reservations/:id`.
 * @param id - Reservation id
 * @param data - The intervention field subset
 */
export async function updateReservationAsAdmin(
  id: string,
  data: AdminReservationIntervention
): Promise<void> {
  if ("driverUserId" in data) {
    if (data.driverUserId === null) {
      await api.put(`/reservations/${id}/unassign`, {});
      return;
    }
    if (data.driverUserId) {
      await api.put(`/reservations/${id}/assign`, { driverUserId: data.driverUserId });
      return;
    }
  }
  await api.put(`/reservations/${id}`, data);
}

/**
 * Issue an optional operator refund from the platform admin reservation detail.
 * The API delegates to ReservationService.refund for money bounds, idempotency,
 * and audit trail; this helper only unwraps the standard response envelope.
 * @param id - Reservation id
 * @param data - Refund amount/reason/idempotency key
 * @returns The post-refund payment state
 */
export async function refundReservationAsAdmin(
  id: string,
  data: AdminReservationRefundRequest
): Promise<AdminReservationRefundResult> {
  const response = await api.post<ApiResponse<AdminReservationRefundResult>>(
    `/reservations/${id}/refund`,
    data
  );
  return response.data.data;
}

/**
 * Send a reservation-scoped platform support message (#835). Recipient
 * resolution is server-side; the client sends only target + content.
 * @param id - Reservation id
 * @param data - Message target and plain-text content
 * @returns Delivery result
 */
export async function sendReservationMessageAsAdmin(
  id: string,
  data: AdminReservationMessageRequest
): Promise<AdminReservationMessageResult> {
  const response = await api.post<ApiResponse<AdminReservationMessageResult>>(
    `/reservations/${id}/message`,
    data
  );
  return response.data.data;
}

// ============================================================================
// Admin notification inbox
// ============================================================================

/** A platform-admin inbox notification. */
export interface AdminNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  resourceType: string | null;
  resourceId: string | null;
  actionUrl: string | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

/** Offset-pagination metadata returned alongside a page of admin notifications. */
export interface AdminNotificationPagination {
  total: number;
  limit: number;
  offset: number;
  /** Whether a next page exists (offset + returned count < total). */
  hasMore: boolean;
}

/**
 * List the current admin's notifications (offset-paginated; #897).
 * @param limit - Page size.
 * @param offset - Row offset to start from (default 0, the first page).
 * @returns The notifications + full pagination (total/limit/offset/hasMore).
 */
export async function getAdminNotifications(
  limit = 10,
  offset = 0
): Promise<{ notifications: AdminNotification[]; pagination: AdminNotificationPagination }> {
  return apiGet(`/notifications?limit=${String(limit)}&offset=${String(offset)}`);
}

/**
 * Get the current admin's unread notification count.
 * @returns The unread count.
 */
export async function getAdminUnreadCount(): Promise<{ count: number }> {
  return apiGet(`/notifications/unread-count`);
}

/**
 * Mark specific admin notifications read.
 * @param notificationIds - IDs to mark read.
 */
export async function markAdminNotificationsRead(notificationIds: string[]): Promise<void> {
  await api.post(`/notifications/mark-read`, { notificationIds });
}

/** Mark all of the current admin's notifications read. */
export async function markAllAdminNotificationsRead(): Promise<void> {
  await api.post(`/notifications/mark-all-read`);
}

// ============================================================================
// Support conversations (admin ↔ company)
// ============================================================================

/** A support thread row in the admin Support inbox. */
export interface AdminSupportConversation {
  id: string;
  companyId: string | null;
  companyName: string | null;
  lastMessageAt: string | null;
  unread: number;
}

/** A message within a support thread. */
export interface AdminSupportMessage {
  id: string;
  body: string;
  createdAt: string;
  senderUserId: string | null;
  senderPlatformAdminId: string | null;
  sender: { id: string; name: string | null } | null;
  senderPlatformAdmin: { id: string; name: string | null } | null;
}

/**
 * List support threads for the admin Support inbox.
 * @returns The support conversations.
 */
export async function getSupportConversations(): Promise<{
  conversations: AdminSupportConversation[];
}> {
  return apiGet(`/conversations`);
}

/**
 * Get the messages in a support thread.
 * @param conversationId - The conversation id.
 * @returns The messages (chronological).
 */
export async function getSupportMessages(
  conversationId: string
): Promise<{ messages: AdminSupportMessage[] }> {
  return apiGet(`/conversations/${conversationId}/messages`);
}

/**
 * Reply to a support thread as the current admin.
 * @param conversationId - The conversation id.
 * @param body - The message text.
 */
export async function sendSupportMessage(conversationId: string, body: string): Promise<void> {
  await api.post(`/conversations/${conversationId}/messages`, { body });
}

/**
 * Mark a support thread read.
 * @param conversationId - The conversation id.
 */
export async function markSupportRead(conversationId: string): Promise<void> {
  await api.post(`/conversations/${conversationId}/read`);
}

// ============================================================================
// Audit log (#284)
// ============================================================================

/** The actor who performed an audited action — a company/driver user, a platform admin, or system-initiated (`null`). */
export interface AuditLogActor {
  kind: "user" | "platformAdmin";
  id: string;
  name: string;
  email: string | null;
}

/** A single platform-wide audit-log row. */
export interface AuditLogEntry {
  id: string;
  action: string;
  resourceType: string;
  resourceId: string | null;
  description: string | null;
  metadata: unknown;
  createdAt: string;
  actor: AuditLogActor | null;
  company: { id: string; name: string } | null;
}

/** Filters accepted by {@link getAuditLog}. */
export interface AuditLogFilters {
  actorUserId?: string;
  actorPlatformAdminId?: string;
  action?: string;
  resourceType?: string;
  resourceId?: string;
  from?: string;
  to?: string;
}

/**
 * Fetch a page of the platform-wide admin-action audit log (#284).
 * @param page - 1-based page
 * @param limit - Page size
 * @param filters - Optional actor/action/resource/date-range filters
 * @returns A paginated audit-log entry list
 */
export async function getAuditLog(
  page = 1,
  limit = 20,
  filters: AuditLogFilters = {}
): Promise<PaginatedResponse<AuditLogEntry>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (filters.actorUserId) params.append("actorUserId", filters.actorUserId);
  if (filters.actorPlatformAdminId)
    params.append("actorPlatformAdminId", filters.actorPlatformAdminId);
  if (filters.action) params.append("action", filters.action);
  if (filters.resourceType) params.append("resourceType", filters.resourceType);
  if (filters.resourceId) params.append("resourceId", filters.resourceId);
  if (filters.from) params.append("from", filters.from);
  if (filters.to) params.append("to", filters.to);
  return apiGet<PaginatedResponse<AuditLogEntry>>(`/audit-log?${params.toString()}`);
}

/** A phone's SMS consent (STOP/START) row on the platform roster (#1522). */
export interface SmsConsentEntry {
  id: string;
  /** Full E.164 phone — platform-admin compliance surface only. */
  phone: string;
  status: "opted_out" | "opted_in";
  /** How the state was recorded: carrier keyword, delivery-error backstop, or in-app. */
  source: string;
  optedOutAt: string | null;
  optedInAt: string | null;
  lastInboundAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Fetch a page of the platform-wide SMS consent (STOP) roster (#1522).
 * Read-only — carrier rules require the subscriber to text START, so there
 * is deliberately no write counterpart.
 * @param page - 1-based page
 * @param limit - Page size
 * @param status - Which consent state to list (defaults to the STOP list)
 * @returns A paginated consent-row list
 */
export async function getSmsConsent(
  page = 1,
  limit = 20,
  status: "opted_out" | "opted_in" = "opted_out"
): Promise<PaginatedResponse<SmsConsentEntry>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit), status });
  return apiGet<PaginatedResponse<SmsConsentEntry>>(`/sms-consent?${params.toString()}`);
}

/** A row on the platform-admin management page (#870) — who + when added. */
export interface PlatformAdminListItem {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  /** Last successful platform-admin login, or `null` if never logged in. */
  lastLoginAt: string | null;
  isActive: boolean;
}

/**
 * Fetch a page of platform admins (#870): who they are and when they were
 * added. Read-only — there is no add/remove-admin mutation endpoint yet.
 * @param page - 1-based page
 * @param limit - Page size
 * @param sort - Optional column sort (name/email/createdAt)
 * @returns A paginated platform-admin list
 */
export async function getPlatformAdmins(
  page = 1,
  limit = 20,
  sort?: SortParam
): Promise<PaginatedResponse<PlatformAdminListItem>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  appendSort(params, sort);
  return apiGet<PaginatedResponse<PlatformAdminListItem>>(`/admins?${params.toString()}`);
}

// ============================================================================
// Test Accounts panel (#1451) — dev/staging test-tooling ONLY. The API mounts
// /api/platform/test-accounts/* only when testApiAccess(env) !== "closed"
// (never in prod), so every function here can 404 by design — callers use
// `isNotFoundError` (@/lib/apiError) as the capability probe, never a
// hardcoded environment check.
// ============================================================================

/**
 * The panel's OWN `[auth.sms.test_otp]` sub-block (1030-1039) — mirrors the
 * server's `RESERVED_TEST_OTP_PHONES` enum (platformTestAccounts.controller.ts),
 * deliberately DISJOINT from the 1000-1020 e2e/mobile/seed-staging pool so the
 * panel can't clobber a seed-staging persona (#1451). Client-side, this only
 * drives the `<select>` options; the server independently re-validates against
 * the same list regardless of what's sent.
 */
export const RESERVED_TEST_OTP_PHONES = [
  "+14155551030",
  "+14155551031",
  "+14155551032",
  "+14155551033",
  "+14155551034",
  "+14155551035",
  "+14155551036",
  "+14155551037",
  "+14155551038",
  "+14155551039",
] as const;

/**
 * The panel's OWN reserved domain (`@panel-test.local`) — mirrors the server's
 * `RESERVED_TEST_DOMAINS`. Disjoint from `@e2e-test.local` / `@harness.local`,
 * so the panel only ever creates + cleans up its own accounts (#1451).
 */
export const RESERVED_TEST_DOMAINS = ["@panel-test.local"] as const;
export type ReservedTestDomain = (typeof RESERVED_TEST_DOMAINS)[number];

/** The default password suggested for every password-variant persona (server applies the same default when omitted). */
export const DEFAULT_TEST_PASSWORD = "TestPassword123!";

/** A newly-created test account's one-time credential — rendered once, never re-fetchable. */
export interface TestAccountCredential {
  userId: string;
  email: string;
  name: string;
  phone?: string;
  companyId?: string;
  companySlug?: string;
  subscriptionTierId?: string;
  dualPersona?: boolean;
  /** Present for password variants and every company-driver create (OTP drivers keep dual login). */
  password?: string;
  /** Present for otp-variant creates (operators/solo-drivers/company-drivers) — always the static test code. */
  otpCode?: string;
}

/** A row on the Test Accounts panel list — a test-domain user + their company memberships. */
export interface TestAccountListItem {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  createdAt: string;
  companies: { id: string; name: string; slug: string; roles: string[] }[];
}

export type TestAccountResetEligibilitySource = "panel_email_domain" | "field_phone_allowlist";

export interface TestAccountResetCompany {
  id: string;
  name: string;
  slug: string;
  roles: string[];
  memberCount: number;
  isTestCompany: boolean;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  stripeAccountId: string | null;
}

export interface TestAccountResetBlocker {
  code:
    | "identity_not_allowlisted"
    | "identity_mismatch"
    | "platform_admin"
    | "protected_fixture"
    | "surviving_company_membership"
    | "active_driver_reservations"
    | "owned_company_active_reservations"
    | "checkout_ledger_requires_1578"
    | "billing_provider_state_requires_1578"
    | "live_billing_provider_state"
    | "ride_payment_history"
    | "storage_provider_unavailable"
    | "storage_inventory_limit_exceeded"
    | "invalid_storage_ownership"
    | "foreign_authored_history"
    | "surviving_company_driver_approval"
    | "provider_mutation_in_progress"
    | "provider_mutation_reconciliation_required";
  message: string;
}

export interface TestAccountResetPreflight {
  userId: string;
  eligible: boolean;
  eligibilitySource: TestAccountResetEligibilitySource | null;
  confirmationPhrase: string;
  identity: {
    email: string;
    phone: string | null;
    phoneVerified: boolean;
    isActive: boolean;
  };
  impacts: {
    appUser: boolean;
    authIdentity: boolean;
    storageObjects: number;
    companies: number;
    reservations: {
      customer: number;
      activeDriver: number;
      historicalDriver: number;
      ownedCompanyTotal: number;
      ownedCompanyActive: number;
    };
    grants: number;
  };
  billing: {
    companyStripeReferences: {
      companyId: string;
      customerId: string | null;
      subscriptionId: string | null;
      accountId: string | null;
      subscriptionItemIds?: string[];
      providerMode: "test" | "live" | "unknown";
      /** #1578: whether classify() confirmed this reference is safe to reconcile/delete. */
      cleanable: boolean;
    }[];
    riderStripeReferences: string[];
    checkoutLedgerRows: number;
    ridePaymentRows: number;
    /** #1578: whether every owned company's Checkout ledger rows classify as cleanable. */
    checkoutLedgerCleanable: boolean;
  };
  companies: TestAccountResetCompany[];
  retained: string[];
  deleted: string[];
  skipped: string[];
  blockers: TestAccountResetBlocker[];
  retryingFromApplicationDeletion: boolean;
}

export type TestAccountResetPhase =
  | "quarantine"
  | "session_revocation"
  | "storage"
  | "billing"
  | "application"
  | "auth"
  | "verification"
  | "verified";

export interface TestAccountResetResult {
  status: "completed" | "partial";
  phase: TestAccountResetPhase;
  retrySafe: true;
  samePhoneRegistrationReady: boolean;
  released: {
    appUser: boolean;
    authIdentity: boolean;
    email: boolean;
    phone: boolean;
    storage: boolean;
  };
  errorCode?: string;
}

export interface ResetTestAccountInput {
  confirmation: string;
  reason: string;
}

// ---------------------------------------------------------------------------
// Auth-only orphan recovery (#1625) — a Supabase auth.users identity whose
// public.users row is provably absent, unrecoverable through the #1613 reset
// tool above (which starts its lookup from public.users).
// ---------------------------------------------------------------------------

export type AuthOrphanRecoveryIdentifierType = "phone" | "email";

export interface AuthOrphanRecoveryIdentifier {
  phone?: string;
  email?: string;
}

export type AuthOrphanRecoveryBlockerCode =
  | "identity_not_found"
  | "app_user_exists"
  | "platform_admin"
  | "protected_fixture"
  | "non_native_identity"
  | "existing_reset_attempt_handoff"
  | "storage_provider_unavailable"
  | "storage_inventory_limit_exceeded";

export interface AuthOrphanRecoveryBlocker {
  code: AuthOrphanRecoveryBlockerCode;
  message: string;
}

export type AuthOrphanRecoveryPhase =
  | "preflight"
  | "session_revocation"
  | "storage"
  | "auth"
  | "verification"
  | "verified";

export interface AuthOrphanRecoveryPreflight {
  found: boolean;
  eligible: boolean;
  identifierType: AuthOrphanRecoveryIdentifierType;
  confirmationPhrase: string | null;
  identity: { email: string | null; phone: string | null } | null;
  impacts: { authIdentity: boolean; storageObjects: number };
  blockers: AuthOrphanRecoveryBlocker[];
  retryingFromPhase: AuthOrphanRecoveryPhase | null;
}

export interface AuthOrphanRecoveryResult {
  status: "completed" | "partial";
  phase: AuthOrphanRecoveryPhase;
  retrySafe: true;
  sameIdentifierRegistrationReady: boolean;
  released: {
    sessions: boolean;
    storage: boolean;
    authIdentity: boolean;
    identifier: boolean;
  };
  errorCode?: string;
}

export interface RecoverAuthOrphanInput extends AuthOrphanRecoveryIdentifier {
  confirmation: string;
  reason: string;
}

/**
 * Mutation-free lookup + eligibility preview for exactly one exact
 * server-allowlisted phone or `@panel-test.local` email. Uniform response
 * shape whether the identifier is unmatched, matched-but-refused, or
 * matched-and-eligible (non-enumerating).
 * @param identifier - Exactly one of `phone` or `email`.
 * @returns The server-authoritative eligibility preview.
 */
export async function previewAuthOrphanRecovery(
  identifier: AuthOrphanRecoveryIdentifier
): Promise<AuthOrphanRecoveryPreflight> {
  const response = await api.post<ApiResponse<AuthOrphanRecoveryPreflight>>(
    "/test-account-orphans/preflight",
    identifier
  );
  return response.data.data;
}

/**
 * Execute a preflight-confirmed Auth-only orphan recovery. The API derives
 * actor and eligibility; the browser sends only the identifier, confirmation,
 * and an audit reason. A 428 is handled by the shared step-up interceptor.
 * @param input - Exactly one of `phone`/`email`, plus exact confirmation and reason.
 * @returns Completed release proof or a retry-safe partial phase.
 */
export async function recoverAuthOrphan(
  input: RecoverAuthOrphanInput
): Promise<AuthOrphanRecoveryResult> {
  const response = await api.post<ApiResponse<AuthOrphanRecoveryResult>>(
    "/test-account-orphans/recover",
    input
  );
  return response.data.data;
}

export type TestCompanyScenarioId =
  | "baseline"
  | "business"
  | "business_plus"
  | "business_ultimate"
  | "trial_active"
  | "trial_expired"
  | "complimentary_active"
  | "complimentary_revoked"
  | "ended"
  | "free_driver"
  | "paid_driver"
  | "past_due";

export interface TestCompanyScenarioBlocker {
  code:
    | "company_not_found"
    | "unsupported_company_tier"
    | "unsupported_company_persona"
    | "no_company_members"
    | "inactive_company_member"
    | "identity_not_allowlisted"
    | "identity_mismatch"
    | "platform_admin"
    | "protected_fixture"
    | "stripe_customer"
    | "stripe_subscription"
    | "stripe_connect"
    | "stripe_connect_history"
    | "billing_event_history"
    | "addon_subscription_item"
    | "checkout_clearance"
    | "checkout_attempt"
    | "rider_vault"
    | "reservation_provider_reference"
    | "ride_payment_history"
    | "stripe_ledger"
    | "live_provider_mode"
    | "unknown_provider_mode"
    | "provider_mutation_in_progress"
    | "provider_mutation_reconciliation_required";
  message: string;
  memberId?: string;
}

/** Server-authoritative entitlement and mutation availability for one test company. */
export interface TestCompanyScenarioSnapshot {
  company: {
    id: string;
    name: string;
    slug: string;
    kind: "operator" | "solo_driver" | null;
  };
  classification: "synthetic" | "provider_backed" | "ineligible";
  eligible: boolean;
  blockers: TestCompanyScenarioBlocker[];
  currentScenario: TestCompanyScenarioId | null;
  entitlement: {
    state: "trialing" | "pending_payment" | "active" | "past_due" | "ended";
    source: "trial" | "stripe_subscription" | "comp_grant" | "legacy" | "none";
  };
  tier: {
    id: string;
    displayName: string;
    features: Record<string, boolean>;
    limits: Record<string, number | null>;
  };
  bookability: {
    configured: boolean;
    effective: boolean;
  };
  availableScenarios: {
    id: TestCompanyScenarioId;
    label: string;
    mutable: boolean;
    confirmationPhrase: string | null;
  }[];
  cache: {
    localInvalidated: boolean;
    readyAt: string | null;
  };
}

export interface ApplyTestCompanyScenarioInput {
  scenario: TestCompanyScenarioId;
  confirmation: string;
  reason: string;
}

export interface CreateTestOperatorInput {
  name?: string;
  variant?: "password" | "otp";
  email?: string;
  password?: string;
  phone?: (typeof RESERVED_TEST_OTP_PHONES)[number];
  dualPersona?: boolean;
}

/**
 * Create a test operator company + admin member.
 * @param input - Persona fields; every field is optional (the server applies
 *   sensible defaults — random `@e2e-test.local` email, `TestPassword123!`).
 * @returns The created account's one-time credential.
 */
export async function createTestOperator(
  input: CreateTestOperatorInput = {}
): Promise<TestAccountCredential> {
  const response = await api.post<ApiResponse<TestAccountCredential>>(
    "/test-accounts/operators",
    input
  );
  return response.data.data;
}

export interface CreateTestSoloDriverInput {
  name?: string;
  tier?: "free_driver" | "paid_driver";
  variant?: "password" | "otp";
  email?: string;
  password?: string;
  phone?: (typeof RESERVED_TEST_OTP_PHONES)[number];
}

/**
 * Create an approved test solo driver.
 * @param input - Persona fields; every field is optional (defaults to
 *   `free_driver` + a random `@e2e-test.local` email).
 * @returns The created account's one-time credential.
 */
export async function createTestSoloDriver(
  input: CreateTestSoloDriverInput = {}
): Promise<TestAccountCredential> {
  const response = await api.post<ApiResponse<TestAccountCredential>>(
    "/test-accounts/solo-drivers",
    input
  );
  return response.data.data;
}

export interface CreateTestRiderInput {
  name?: string;
  email?: string;
  password?: string;
  phone?: (typeof RESERVED_TEST_OTP_PHONES)[number];
}

/**
 * Create a plain test rider (app user, no company).
 * @param input - Persona fields; every field is optional.
 * @returns The created account's one-time credential.
 */
export async function createTestRider(
  input: CreateTestRiderInput = {}
): Promise<TestAccountCredential> {
  const response = await api.post<ApiResponse<TestAccountCredential>>(
    "/test-accounts/riders",
    input
  );
  return response.data.data;
}

export interface AddTestCompanyDriverInput {
  companyId: string;
  name?: string;
  variant?: "password" | "otp";
  email?: string;
  password?: string;
  phone?: (typeof RESERVED_TEST_OTP_PHONES)[number];
}

/**
 * Add a driver-role member to an EXISTING company.
 * @param input - `companyId` is required; name/login variant/email/password/phone are optional.
 * @returns The created account's one-time credential.
 */
export async function addTestCompanyDriver(
  input: AddTestCompanyDriverInput
): Promise<TestAccountCredential> {
  const response = await api.post<ApiResponse<TestAccountCredential>>(
    "/test-accounts/company-drivers",
    input
  );
  return response.data.data;
}

/**
 * List every panel-domain test account, or look up the account attached to one
 * exact server-allowlisted field-test phone. With no phone this remains the
 * capability probe: a 404 means the panel is disabled for this environment.
 * @param phone - Optional exact phone; the server normalizes it and rejects
 *   every number outside its private allowlist.
 * @returns The current test accounts.
 */
export async function listTestAccounts(phone?: string): Promise<TestAccountListItem[]> {
  const normalizedInput = phone?.trim();
  const query = normalizedInput
    ? `?${new URLSearchParams({ phone: normalizedInput }).toString()}`
    : "";
  return apiGet<TestAccountListItem[]>(`/test-accounts${query}`);
}

/**
 * Load the server-authoritative reset impact and blocker preview.
 * @param userId - Target application/Auth UUID from the list response.
 * @returns Eligibility, impacts, blockers, and exact confirmation phrase.
 */
export async function getTestAccountResetPreflight(
  userId: string
): Promise<TestAccountResetPreflight> {
  return apiGet<TestAccountResetPreflight>(
    `/test-accounts/${encodeURIComponent(userId)}/reset-preflight`
  );
}

/**
 * Execute a preflight-confirmed reset. The API derives actor, eligibility,
 * affected records, and deletion scope; the browser sends only confirmation
 * and an audit reason. A 428 is handled by the shared step-up interceptor.
 * @param userId - Target application/Auth UUID from the list response.
 * @param input - Exact phrase plus meaningful audit reason.
 * @returns Completed release proof or a retry-safe partial phase.
 */
export async function resetTestAccount(
  userId: string,
  input: ResetTestAccountInput
): Promise<TestAccountResetResult> {
  const response = await api.post<ApiResponse<TestAccountResetResult>>(
    `/test-accounts/${encodeURIComponent(userId)}/reset`,
    input
  );
  return response.data.data;
}

/**
 * Load authoritative entitlement truth and mutable scenarios for a test-owned company.
 * @param companyId - Company UUID returned by the test-account list.
 * @returns Current effective entitlement, blockers, scenarios, and cache state.
 */
export async function getTestCompanyScenario(
  companyId: string
): Promise<TestCompanyScenarioSnapshot> {
  return apiGet<TestCompanyScenarioSnapshot>(
    `/test-accounts/companies/${encodeURIComponent(companyId)}/scenario`
  );
}

/**
 * Apply one server-defined synthetic scenario after exact confirmation.
 * @param companyId - Company UUID returned by the test-account list.
 * @param input - Closed scenario id, server-provided confirmation, and audit reason.
 * @returns Fresh server-authoritative entitlement truth after commit.
 */
export async function applyTestCompanyScenario(
  companyId: string,
  input: ApplyTestCompanyScenarioInput
): Promise<TestCompanyScenarioSnapshot> {
  const response = await api.put<ApiResponse<TestCompanyScenarioSnapshot>>(
    `/test-accounts/companies/${encodeURIComponent(companyId)}/scenario`,
    input
  );
  return response.data.data;
}

/** One test-owned company whose deletion was skipped and why (#1578). */
export interface RetainedTestCompany {
  companyId: string;
  reason: string;
}

export interface CleanupTestAccountsResult {
  deletedCount: number;
  emails: string[];
  /**
   * #1578: owned companies NOT deleted because their Checkout ledger/Stripe
   * state could not be provably reconciled as test-mode. The company row
   * (and its ledger history) survives, member-less; empty in the common
   * case (no Checkout ledger involvement).
   */
  retainedCompanies: RetainedTestCompany[];
}

/**
 * Delete every test account on the given reserved domain.
 * @param domain - Must be one of {@link RESERVED_TEST_DOMAINS} — the server
 *   rejects anything else with 400.
 * @returns The count, list of deleted emails, and any retained companies.
 */
export async function cleanupTestAccounts(
  domain: ReservedTestDomain
): Promise<CleanupTestAccountsResult> {
  const response = await api.delete<ApiResponse<CleanupTestAccountsResult>>("/test-accounts", {
    data: { domain },
  });
  return response.data.data;
}
